import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from './AuthContext';
import { prayerNames } from '../data/prayerData';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const TOKEN_KEY = 'prayer_app_token';

// Helper to get auth headers
const getAuthHeaders = () => {
  const token = localStorage.getItem(TOKEN_KEY);
  if (token) {
    return { 'Authorization': `Bearer ${token}` };
  }
  return {};
};

const TrackerContext = createContext(null);

export const useTracker = () => {
  const context = useContext(TrackerContext);
  if (!context) {
    throw new Error('useTracker must be used within a TrackerProvider');
  }
  return context;
};

export const TrackerProvider = ({ children }) => {
  const { isAuthenticated, user } = useAuth();
  const [todayPrayers, setTodayPrayers] = useState({});
  const [weeklyStats, setWeeklyStats] = useState([]);
  const [streak, setStreak] = useState(0);
  const [loading, setLoading] = useState(false);
  const [currentDateKey, setCurrentDateKey] = useState('');
  const lastDateKeyRef = useRef('');

  const getTodayKey = useCallback(() => {
    const today = new Date();
    return `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
  }, []);

  // Initialize empty prayers for a new day
  const getEmptyPrayers = useCallback(() => {
    const initial = {};
    prayerNames.forEach(p => { initial[p.key] = false; });
    return initial;
  }, []);

  // Load from localStorage for non-authenticated users
  const loadLocalData = useCallback(() => {
    const todayKey = getTodayKey();
    setCurrentDateKey(todayKey);
    
    const stored = localStorage.getItem(`prayers_${todayKey}`);
    if (stored) {
      setTodayPrayers(JSON.parse(stored));
    } else {
      // New day - start fresh
      setTodayPrayers(getEmptyPrayers());
    }
    
    // Calculate streak from local storage
    let currentStreak = 0;
    const checkDate = new Date();
    for (let i = 0; i < 30; i++) {
      const key = `${checkDate.getFullYear()}-${String(checkDate.getMonth() + 1).padStart(2, '0')}-${String(checkDate.getDate()).padStart(2, '0')}`;
      const dayData = localStorage.getItem(`prayers_${key}`);
      if (dayData) {
        const prayers = JSON.parse(dayData);
        const allCompleted = Object.values(prayers).every(v => v === true);
        if (allCompleted) {
          currentStreak++;
          checkDate.setDate(checkDate.getDate() - 1);
        } else if (i === 0) {
          // Today not complete yet, check yesterday
          checkDate.setDate(checkDate.getDate() - 1);
        } else {
          break;
        }
      } else {
        break;
      }
    }
    setStreak(currentStreak);
  }, [getTodayKey, getEmptyPrayers]);

  // Fetch from backend for authenticated users
  const fetchTrackerData = useCallback(async () => {
    if (!isAuthenticated) return;
    
    const todayKey = getTodayKey();
    setCurrentDateKey(todayKey);
    setLoading(true);
    
    try {
      // Send local date to backend
      const response = await fetch(`${BACKEND_URL}/api/tracker/today?date=${todayKey}`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        
        // Check if the returned date matches today
        if (data.date === todayKey) {
          setTodayPrayers(data.prayers || getEmptyPrayers());
        } else {
          // Date mismatch - start fresh for today
          setTodayPrayers(getEmptyPrayers());
        }
        setStreak(data.streak || 0);
        
        // Also save to localStorage for offline/cross-browser support
        localStorage.setItem(`prayers_${todayKey}`, JSON.stringify(data.prayers || getEmptyPrayers()));
      } else {
        // Fallback to local data if API fails
        loadLocalData();
      }
    } catch (error) {
      console.error('Failed to fetch tracker data:', error);
      loadLocalData();
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, loadLocalData, getTodayKey, getEmptyPrayers]);

  // Check for day change every minute
  useEffect(() => {
    const checkDayChange = () => {
      const todayKey = getTodayKey();
      if (lastDateKeyRef.current && lastDateKeyRef.current !== todayKey) {
        console.log('New day detected, resetting tracker...');
        // Day has changed - reset tracker
        setTodayPrayers(getEmptyPrayers());
        setCurrentDateKey(todayKey);
        
        if (isAuthenticated) {
          fetchTrackerData();
        } else {
          loadLocalData();
        }
      }
      lastDateKeyRef.current = todayKey;
    };

    // Check immediately
    checkDayChange();
    
    // Check every minute
    const interval = setInterval(checkDayChange, 60000);
    
    return () => clearInterval(interval);
  }, [getTodayKey, getEmptyPrayers, isAuthenticated, fetchTrackerData, loadLocalData]);

  useEffect(() => {
    if (isAuthenticated) {
      fetchTrackerData();
    } else {
      loadLocalData();
    }
  }, [isAuthenticated, fetchTrackerData, loadLocalData]);

  const togglePrayer = async (prayerKey) => {
    const todayKey = getTodayKey();
    const newValue = !todayPrayers[prayerKey];
    const updatedPrayers = { ...todayPrayers, [prayerKey]: newValue };
    setTodayPrayers(updatedPrayers);
    localStorage.setItem(`prayers_${todayKey}`, JSON.stringify(updatedPrayers));
    if (isAuthenticated) {
      try {
        await fetch(`${BACKEND_URL}/api/tracker/prayer`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
          body: JSON.stringify({ prayer: prayerKey, completed: newValue, date: todayKey }),
        });
        fetchTrackerData();
      } catch (error) { console.error('Failed to update prayer:', error); }
    } else { loadLocalData(); }
  };

  /**
   * batchTogglePrayers — atomically mark multiple prayers at once.
   * Fixes the stale-state race condition when marking Jama' prayer pairs.
   */
  const batchTogglePrayers = async (keys) => {
    const todayKey = getTodayKey();
    // Read the most recent state and apply all toggles atomically
    const currentPrayers = JSON.parse(localStorage.getItem(`prayers_${todayKey}`) || JSON.stringify(getEmptyPrayers()));
    const updatedPrayers = { ...currentPrayers };
    // All keys get the SAME new value (completion state of the primary key)
    const newValue = !currentPrayers[keys[0]];
    keys.forEach(k => { updatedPrayers[k] = newValue; });
    setTodayPrayers(updatedPrayers);
    localStorage.setItem(`prayers_${todayKey}`, JSON.stringify(updatedPrayers));
    if (isAuthenticated) {
      try {
        await Promise.all(keys.map(k =>
          fetch(`${BACKEND_URL}/api/tracker/prayer`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
            body: JSON.stringify({ prayer: k, completed: newValue, date: todayKey }),
          })
        ));
        fetchTrackerData();
      } catch (error) { console.error('Failed to batch update prayers:', error); }
    } else { loadLocalData(); }
  };

  const completedCount = Object.values(todayPrayers).filter(Boolean).length;
  const totalPrayers = prayerNames.length;
  const progress = (completedCount / totalPrayers) * 100;

  return (
    <TrackerContext.Provider
      value={{
        todayPrayers,
        weeklyStats,
        streak,
        loading,
        togglePrayer,
        batchTogglePrayers,
        completedCount,
        totalPrayers,
        progress,
        currentDateKey,
        fetchTrackerData,
      }}
    >
      {children}
    </TrackerContext.Provider>
  );
};
